import { useEffect, useRef, useState } from "react";
import { getCliente } from "@/features/auxiliar/clientes/api/get-cliente";

export type VentaDraft = {
    id: string;
    savedAt: string;
    itemCount: number;
    clienteNombre?: string;
    values: any;
};

const MAX_DRAFTS = 10;
const DEBOUNCE_MS = 1500;

function getOrganizacionId(): string {
    try {
        const auth = localStorage.getItem("auth");
        if (!auth) return "unknown";
        const { token } = JSON.parse(auth);
        if (!token) return "unknown";
        const payload = JSON.parse(atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
        return payload["OrganizacionId"] ?? "unknown";
    } catch {
        return "unknown";
    }
}

// Lectura/escritura aisladas: cualquier error de localStorage se logea pero
// nunca se propaga, para no afectar el flujo de creación de la venta.
function safeReadDrafts(key: string): VentaDraft[] {
    try {
        const item = localStorage.getItem(key);
        if (!item) return [];
        const parsed = JSON.parse(item);
        return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
        console.error("Error al leer los borradores de venta del localStorage:", e);
        return [];
    }
}

function safeWriteDrafts(key: string, drafts: VentaDraft[]): void {
    try {
        localStorage.setItem(key, JSON.stringify(drafts));
    } catch (e) {
        console.error("Error al guardar el borrador de venta en el localStorage:", e);
    }
}

export function useVentaDraft(form: any) {
    const storageKey = `venta-drafts-${getOrganizacionId()}`;

    const [draftId] = useState(() => crypto.randomUUID());
    const [drafts, setDraftsState] = useState<VentaDraft[]>(() => safeReadDrafts(storageKey));
    const draftsRef = useRef(drafts);
    draftsRef.current = drafts;

    const setDrafts = (next: VentaDraft[]) => {
        safeWriteDrafts(storageKey, next);
        setDraftsState(next);
    };

    const clienteNombreRef = useRef<string | undefined>(undefined);
    const lastClienteIdRef = useRef<string | undefined>(undefined);
    const lastItemsCountRef = useRef<number>(0);
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const saveDraft = (values: any, items: any[]) => {
        const newDraft: VentaDraft = {
            id: draftId,
            savedAt: new Date().toISOString(),
            itemCount: items.length,
            clienteNombre: clienteNombreRef.current,
            values,
        };
        const prev = draftsRef.current ?? [];
        const withoutCurrent = prev.filter(d => d.id !== draftId);
        const updated = [...withoutCurrent, newDraft];
        setDrafts(updated.length > MAX_DRAFTS ? updated.slice(-MAX_DRAFTS) : updated);
    };

    useEffect(() => {
        const unsubscribe = form.store.subscribe(() => {
            const values = form.store.state.values;
            const items = values?.items;
            const clienteId = values?.clienteId;

            // Cuando cambia el clienteId, fetch del nombre en paralelo
            if (clienteId && clienteId !== lastClienteIdRef.current) {
                lastClienteIdRef.current = clienteId;
                clienteNombreRef.current = undefined;
                getCliente(clienteId)
                    .then(c => { clienteNombreRef.current = c.nombre; })
                    .catch(() => { });
            } else if (!clienteId) {
                lastClienteIdRef.current = undefined;
                clienteNombreRef.current = undefined;
            }

            if (!Array.isArray(items) || items.length === 0) {
                lastItemsCountRef.current = 0;
                if (debounceRef.current) {
                    clearTimeout(debounceRef.current);
                    debounceRef.current = null;
                }
                return;
            }

            lastItemsCountRef.current = items.length;

            if (debounceRef.current) clearTimeout(debounceRef.current);

            debounceRef.current = setTimeout(() => saveDraft(values, items), DEBOUNCE_MS);
        });

        return () => {
            unsubscribe();
            if (debounceRef.current) clearTimeout(debounceRef.current);
        };
    }, []);

    const deleteCurrentDraft = () => {
        setDrafts((draftsRef.current ?? []).filter(d => d.id !== draftId));
    };

    const deleteDraft = (id: string) => {
        setDrafts((draftsRef.current ?? []).filter(d => d.id !== id));
    };

    const otherDrafts = (drafts ?? []).filter(d => d.id !== draftId);

    return { draftId, otherDrafts, deleteCurrentDraft, deleteDraft };
}
